/**
 * The interview status machine, on the client, and the words for the interview
 * vocabulary — statuses, kinds, panel roles and scorecard recommendations.
 *
 * The interview analogue of `web/src/recruiting/application.ts`, treated the same way:
 * the status set a scheduled interview offers is **derived from this table**, so an
 * illegal move has no button rather than a disabled one, and the server's `409
 * WORKFORCE_INVALID_STATE_TRANSITION` — carrying `details.allowed_transitions[]` — is
 * what actually decides. This copy exists so the detail can offer the legal moves
 * *before* a refusal; the refusal's list is authoritative and is printed verbatim when
 * the two ever disagree.
 *
 * ── The machine (transcribed from `openapi.yaml`) ────────────────────────────
 *
 *   scheduled  → completed | cancelled | no_show
 *   completed / cancelled / no_show — terminal, no move out
 *
 * The table is transcribed rather than imported for the reason `application.ts` gives:
 * the SPA bundle is served to browsers, and a `web/ → src/modules/` import would drag a
 * `pg` handle into one.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type {
  InterviewKind,
  InterviewStatus,
  PanelRole,
  ScorecardRecommendation,
} from '../api/recruiting';

export {
  INTERVIEW_STATUSES,
  INTERVIEW_KINDS,
  PANEL_ROLES,
  SCORECARD_RECOMMENDATIONS,
} from '../api/recruiting';
export type {
  InterviewStatus,
  InterviewKind,
  PanelRole,
  ScorecardRecommendation,
} from '../api/recruiting';

/**
 * Every status has a row, the terminal three included, whose row is the empty list.
 * An absent key and an empty list are different statements; keeping the empty rows
 * makes "is this terminal" a fact read off the table rather than an omission.
 */
export const INTERVIEW_TRANSITIONS: Readonly<Record<InterviewStatus, readonly InterviewStatus[]>> =
  {
    scheduled: ['completed', 'cancelled', 'no_show'],
    completed: [],
    cancelled: [],
    no_show: [],
  };

export function interviewTransitions(from: InterviewStatus): readonly InterviewStatus[] {
  return INTERVIEW_TRANSITIONS[from];
}

/** `true` for exactly the statuses with no outgoing edge. Derived, never listed. */
export function isTerminalInterviewStatus(status: InterviewStatus): boolean {
  return INTERVIEW_TRANSITIONS[status].length === 0;
}

/** The name of a status, for the badge. */
export const INTERVIEW_STATUS_LABEL: Readonly<Record<InterviewStatus, MessageKey>> = {
  scheduled: 'recruiting.interviews.status.scheduled',
  completed: 'recruiting.interviews.status.completed',
  cancelled: 'recruiting.interviews.status.cancelled',
  no_show: 'recruiting.interviews.status.no_show',
};

/** What pressing a status control **does**, as a verb — not the name of the status. */
export const INTERVIEW_STATUS_MOVE_LABEL: Readonly<Record<InterviewStatus, MessageKey>> = {
  scheduled: 'recruiting.interviews.move.scheduled',
  completed: 'recruiting.interviews.move.completed',
  cancelled: 'recruiting.interviews.move.cancelled',
  no_show: 'recruiting.interviews.move.no_show',
};

/**
 * The badge severity a status is drawn at. A live schedule is `info`; a completed
 * interview `low` (done, benign); a cancelled/no-show `medium` (something to notice).
 * Icon and text carry it, as everywhere; colour is the third channel (DESIGN.md §4).
 */
export const INTERVIEW_STATUS_BADGE: Readonly<Record<InterviewStatus, Severity>> = {
  scheduled: 'info',
  completed: 'low',
  cancelled: 'medium',
  no_show: 'medium',
};

/** The name of an interview kind, for the row and the schedule form. */
export const INTERVIEW_KIND_LABEL: Readonly<Record<InterviewKind, MessageKey>> = {
  phone: 'recruiting.interviews.kind.phone',
  onsite: 'recruiting.interviews.kind.onsite',
  technical: 'recruiting.interviews.kind.technical',
  panel: 'recruiting.interviews.kind.panel',
  culture: 'recruiting.interviews.kind.culture',
};

/** The name of a panel role. */
export const PANEL_ROLE_LABEL: Readonly<Record<PanelRole, MessageKey>> = {
  lead: 'recruiting.interviews.role.lead',
  panelist: 'recruiting.interviews.role.panelist',
};

/** The name of a scorecard recommendation. */
export const RECOMMENDATION_LABEL: Readonly<Record<ScorecardRecommendation, MessageKey>> = {
  strong_yes: 'recruiting.interviews.recommendation.strong_yes',
  yes: 'recruiting.interviews.recommendation.yes',
  no: 'recruiting.interviews.recommendation.no',
  strong_no: 'recruiting.interviews.recommendation.strong_no',
};

/**
 * The badge severity a recommendation is drawn at — how strong a hire signal it is.
 * A `strong_yes`/`yes` reads `low` (all-clear); a `no`/`strong_no` `high`.
 */
export const RECOMMENDATION_BADGE: Readonly<Record<ScorecardRecommendation, Severity>> = {
  strong_yes: 'low',
  yes: 'low',
  no: 'high',
  strong_no: 'high',
};
